/**
 * The hero at the top of the Metal Solutions landing pages, on the tertiary background:
 * breadcrumb, `h1` title, and a description, followed by a band in the secondary brand colour.
 * The rest depends on the build's `NEXT_PUBLIC_COMPANY`:
 * - Ryerson: two figure tiles beside the description (`yearsInBusiness` / `metalProducts` with
 *   their labels). `imageUrl` is ignored.
 * - Southern Tool Steel (dormant build): an image (`imageUrl`) beside the description. The figure
 *   fields are ignored.
 *
 * Used by `../BasePage/BasePage.tsx` (the Solutions landing page and the capabilities index),
 * `components/MetalSolutions/MetalSolutionsLandingRyerson.tsx` (`/metal-solutions/`),
 * `src/pages/metal-solutions/industries/index.tsx`, and on the dormant Southern Tool Steel build
 * `components/MetalSolutions/STS/MetalSolutionsLandingSTS.tsx`,
 * `components/MetalSolutions/STS/solutions/MetalSolutionsSolutionsLandingSTS.tsx` and
 * `src/pages/metal-solutions/services/index.tsx`. {@link HeroSectionContent} is the type of the
 * hero copy in the `index.ts` modules of `src/content/ryerson/metal-solutions/` and its
 * `capabilities/`, `industries/` and `solutions/` folders, and in
 * `src/content/sts/metal-solutions/services/index.ts`.
 */
import React from "react";
import { ContentSection } from "@ryerson/frontend.layout";
import { Grid, Column, Row } from "@ryerson/frontend.layout";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { Typography } from "@ryerson/frontend.typography";
import { ITheme, useTheme } from "@ryerson/frontend.theme";
import { Breadcrumb } from "@ryerson/frontend.navigation";
import { Media } from "@ryerson/frontend.next";
import Spacer from "@components/Shared/Spacer/Spacer";
import { Company } from "@ryerson/common.enums";

/** One language's hero copy. Which optional fields render depends on the brand (file header). */
export type HeroSectionContent = {
	/** The page's `h1`; also the alt text of the Southern Tool Steel image. */
	title: string;
	description: string;
	/** Ryerson only: the first figure tile's large text, as displayed (for example `"180+"`). */
	yearsInBusiness?: string;
	/** Ryerson only: the second figure tile's large text, as displayed (`"70,000+"`). */
	metalProducts?: string;
	/** Ryerson only: caption under `yearsInBusiness`. */
	yearsInBusinessLabel?: string;
	/** Ryerson only: caption under `metalProducts`. */
	metalProductsLabel?: string;
	/** Southern Tool Steel only: the image beside the description. */
	imageUrl?: string;
};

type HeroSectionProps = {
	content: HeroSectionContent;
};

const BreadcrumbContainer = styled.div`
	margin-top: 32px;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				margin-top: 8px;
			}
		`;
	}}
`;

const Divider = styled.hr`
	display: block;
	width: 100%;
	margin-bottom: 48px;
	margin-top: 25px;
	opacity: 0.1;
`;

const DescriptionContainer = styled.div`
	width: 100%;
	max-width: 465px;
	display: block;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				margin-bottom: 40px;
			}
		`;
	}}
`;

/**
 * The Ryerson figure tiles' large text: heading-sized, but rendered as a `div` (`variant="div"`),
 * so the hero keeps a single `h1`.
 */
const NotH1 = styled(Typography)`
	font-size: 56px;
	margin-top: -20px;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				font-size: 37px;
			}
		`;
	}}
`;

const ImageContainer = styled.div`
	display: block;
	width: auto;
	height: auto;
	margin: 0 auto;
`;

const cssSpacer = (theme: ITheme) => css`
	background-color: ${theme?.colors?.primary?.secondaryBrand};
	height: 94px;
	@media only screen and (max-width: ${theme.breakpoints.lg}) {
		height: 46px;
	}
`;

/** The Metal Solutions hero; see the file header for the per-brand layouts. */
const SolutionsHero: React.FC<HeroSectionProps> = ({ content }) => {
	const { theme } = useTheme();
	const company = process.env.NEXT_PUBLIC_COMPANY;

	return (
		<ContentSection type="tertiary">
			<BreadcrumbContainer theme={theme}>
				<Breadcrumb type="tertiary" size="xs" />
			</BreadcrumbContainer>
			{/*
			 * Southern Tool Steel layout first; every other company gets the Ryerson layout. In
			 * both title styles, `padding-bottom; 13px;` is not a valid declaration, so it has no
			 * effect.
			 */}
			{company === Company.SOUTHERN_TOOL_STEEL ? (
				<>
					<Typography
						variant="h1"
						type="tertiary"
						css={css`
							margin-top: 67px; 
                    		margin-bottom: 67px; 
                   	 		padding-bottom; 13px; 
                    		@media(max-width:${theme.breakpoints.lg}){
                        		margin-bottom:22px;
								margin-top: 22px;
							}
                		`}
					>
						{content.title}
					</Typography>
					<Media greaterThanOrEqual="lg">
						<Divider css={{ color: theme.colors.primary.white }} />
					</Media>
					<Grid>
						<Row>
							<Column md={6} lg={6}>
								<DescriptionContainer theme={theme}>
									<Typography type="tertiary" size="md">
										{content.description}
									</Typography>
								</DescriptionContainer>
							</Column>
							<Column md={6} lg={6}>
								<ImageContainer
									css={css`
										width: 100%;
										max-width: 100%;
										overflow: hidden;
										@media (max-width: ${theme.breakpoints.md}) {
											max-width: 440px;
											width: 100%;
											margin-left: auto;
											margin-right: 0;
										}
									`}
								>
									<img
										css={css`
											@media (max-width: ${theme.breakpoints.sm}) {
												width: 100%;
												background-size: cover;
												background-repeat: no-repeat;
												background-position: center center;
											}
										`}
										alt={content.title}
										src={content.imageUrl}
									/>
								</ImageContainer>
							</Column>
						</Row>
					</Grid>
				</>
			) : (
				<>
					<Typography
						variant="h1"
						type="tertiary"
						css={css`margin-top: 16px; 
				margin-bottom:50px; 
				padding-bottom; 13px; 
				@media(max-width:${theme.breakpoints.lg}){
					margin-bottom:22px;
				}
			`}
					>
						{content.title}
					</Typography>
					<Media greaterThanOrEqual="lg">
						<Divider css={{ color: theme.colors.primary.white }} />
					</Media>
					<Grid>
						<Row>
							<Column md={12} lg={6}>
								<DescriptionContainer theme={theme}>
									<Typography type="tertiary" size="lg">
										{content.description}
									</Typography>
								</DescriptionContainer>
							</Column>
							<Column xs={6} sm={6} md={6} lg={3}>
								<NotH1
									type="tertiary"
									variant="div"
									theme={theme}
									css={{
										fontFamily: theme.typography.fontFamilies.headings,
										color: theme.colors.primary.white,
									}}
								>
									{content.yearsInBusiness}
								</NotH1>
								<Typography type="tertiary" variant="div">
									{content.yearsInBusinessLabel}
								</Typography>
							</Column>
							<Column xs={6} sm={6} md={6} lg={3}>
								<NotH1
									type="tertiary"
									variant="div"
									theme={theme}
									css={{
										fontFamily: theme.typography.fontFamilies.headings,
										color: theme.colors.primary.white,
									}}
								>
									{content.metalProducts}
								</NotH1>
								<Typography type="tertiary" variant="div">
									{content.metalProductsLabel}
								</Typography>
							</Column>
						</Row>
					</Grid>
				</>
			)}
			<Spacer css={cssSpacer(theme)} />
		</ContentSection>
	);
};

export default SolutionsHero;
